import gql from "graphql-tag";
import {
  locationFields,
  organizationFields,
  profileFields,
  userFields,
} from "../../graphql/fragments";

const featureFields = gql`
  fragment FeatureFields on OrganizationFeature {
    disabled
    feature {
      name
    }
  }
`;

// The navigable scope, carried on the login response so the app can derive the
// accessible organizations without a separate myOrganizations round-trip: each
// role's org, its ancestor path, and its descendant sub-tree (a region expands to
// its facilities). Platform/application role orgs return no descendants (bounded
// server-side) — those admins use the lazy rooted tree instead.
const userScopeFields = gql`
  fragment UserScopeFields on User {
    roles {
      organization {
        id
        name
        type
        parentId
        profile {
          ... on FacilityProfile {
            locationCode
          }
        }
        ancestors {
          id
          name
          type
          parentId
        }
        descendants(types: ["organization", "region", "facility"]) {
          id
          name
          type
          parentId
          profile {
            ... on FacilityProfile {
              locationCode
            }
          }
        }
      }
    }
  }
`;

export const AUTHENTICATE_USER = gql`
  mutation AuthenticateUser(
    $username: String!
    $password: String!
    $recaptchaToken: String
  ) {
    login(
      username: $username
      password: $password
      recaptchaToken: $recaptchaToken
    ) {
      token
      twoFactorRequired
      twoFactorSetupRequired
      user {
        ...UserFields
        ...UserScopeFields
        defaultOrganization {
          id
          name
          type
          subtype
          timeZone
        }
      }
    }
  }
  ${userFields}
  ${userScopeFields}
`;

export const GET_CONTACT_METHODS = gql`
  query userMContactMethods($token: String!) {
    userMContactMethods(token: $token) {
      id
      contact
      type
    }
  }
`;

export const INITIATE_TWO_FACTOR_ACTIVATION = gql`
  mutation initiateTwoFactorActivation(
    $token: String!
    $contactMethodId: String!
  ) {
    initiateTwoFactorActivation(
      token: $token
      contactMethodId: $contactMethodId
    ) {
      contactMethod {
        contact
        id
        type
      }
      message
    }
  }
`;

export const VALIDATE_OTP_AND_LOGIN = gql`
  mutation validateOtpAndLogin($token: String!, $otp: String!) {
    validateOtpAndLogin(token: $token, otp: $otp) {
      token
      twoFactorRequired
      twoFactorSetupRequired
      user {
        ...UserFields
        ...UserScopeFields
        defaultOrganization {
          id
          name
          type
          subtype
          timeZone
        }
      }
    }
  }
  ${userFields}
  ${userScopeFields}
`;

export const ACTIVATE_TWO_FACTOR_AND_LOGIN = gql`
  mutation activateTwoFactorAndLogin($token: String!, $otp: String!) {
    activateTwoFactorAndLogin(token: $token, otp: $otp) {
      token
      user {
        ...UserFields
        ...UserScopeFields
        defaultOrganization {
          id
          name
          type
          subtype
          timeZone
        }
      }
    }
  }
  ${userFields}
  ${userScopeFields}
`;

export const RESEND_OTP = gql`
  mutation resendOtp($token: String!, $contactMethodId: String) {
    resendOtp(token: $token, contactMethodId: $contactMethodId) {
      contactMethod {
        contact
        id
        type
      }
      message
    }
  }
`;

export const SKIP_TWO_FACTOR_AND_LOGIN = gql`
  mutation skipTwoFactorAndLogin($token: String!) {
    skipTwoFactorAndLogin(token: $token) {
      token
      twoFactorRequired
      twoFactorSetupRequired
      user {
        ...UserFields
        ...UserScopeFields
        defaultOrganization {
          id
          name
          type
          subtype
          timeZone
        }
      }
    }
  }
  ${userFields}
  ${userScopeFields}
`;

export const CREATE_HUBSPOT_CONTACT = gql`
  mutation {
    createHsContact {
      token
    }
  }
`;

export const GET_FACILITY = gql`
  query GetFacility($id: ID!) {
    organization(id: $id) {
      ...OrganizationFields
      features {
        ...FeatureFields
      }
      profile {
        ...ProfileFields
      }
      location {
        spots
      }
      billingProfile {
        smartZoneSubscriptionAmount
        deviceChargeAmount
      }
      facilityOrganization: ancestors(type: "organization") {
        id
        name
        features {
          ...FeatureFields
        }
      }
      organizationDetails {
        playbook
      }
    }
  }
  ${organizationFields}
  ${featureFields}
  ${profileFields}
`;

export const FACILITY_USERS = gql`
  query FacilityUsers($id: ID, $roles: [String]!, $parentId: ID) {
    organizationUsers(id: $id, roles: $roles, parentId: $parentId) {
      id
      firstName
      lastName
      username
    }
  }
`;

export const GET_CS_RANKING = gql`
  query GetCsRanking($organizationId: ID!) {
    csRankingAssignments(organizationId: $organizationId) {
      info {
        owner_user {
          firstName
          lastName
        }
      }
    }
  }
`;

export const UPDATE_FACILITY = gql`
  mutation UpdateFacility(
    $id: ID!
    $location: InputLocation
    $name: String!
    $timeZone: String!
    $type: String!
    $profile: InputFacilityProfile
  ) {
    updateFacility(
      id: $id
      location: $location
      name: $name
      timeZone: $timeZone
      type: $type
      profile: $profile
    ) {
      ...OrganizationFields
      features {
        disabled
        feature {
          name
        }
      }
      location {
        ...LocationFields
      }
      profile {
        ...ProfileFields
      }
    }
  }
  ${locationFields}
  ${organizationFields}
  ${profileFields}
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($username: String!) {
    changePassword(username: $username) {
      status
      message
    }
  }
`;

export const UPDATE_PASSWORD = gql`
  mutation UpdatePassword($password: String!, $token: String!) {
    updatePassword(password: $password, token: $token) {
      status
      message
    }
  }
`;

// Non-consuming check used on the reset page to reject already-used or expired
// links before showing the form.
export const CHANGE_PASSWORD_TOKEN_VALID = gql`
  query ChangePasswordTokenValid($token: String!) {
    changePasswordTokenValid(token: $token)
  }
`;

export const GET_SLACK_CHANNELS = gql`
  query GetSlackChannels($organizationId: String!) {
    slackChannels(organizationId: $organizationId) {
      id
      name
      isPrivate
      numMembers
    }
  }
`;

export const INITIATE_QR_SESSION = gql`
  mutation initiQrSession {
    initQrSession {
      qrToken
    }
  }
`;

export const AUTHENTICATE_WITH_QR_TOKEN = gql`
  mutation authenticateWithQrToken($qrToken: String!) {
    authenticateWithQrToken(qrToken: $qrToken) {
      organization {
        ...OrganizationFields
      }
      token
      user {
        ...UserFields
        ...UserScopeFields
      }
    }
  }
  ${organizationFields}
  ${userFields}
  ${userScopeFields}
`;
